// react-query hooks + query keys for every read, plus mutation hooks that
// invalidate the right slices (session rollup, groups, states, group detail,
// state findings) after any disposition so the progress numbers stay correct.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { api, type StateFindingFilters } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type {
  CommentInput,
  CostDefaults,
  DispositionInput,
  MyCustomer,
  SessionSummary,
} from '../api/types';

export const qk = {
  myCustomers: ['my-customers'] as const,
  session: (cid: string) => ['session', cid] as const,
  groups: (cid: string, sid: string) => ['groups', cid, sid] as const,
  group: (cid: string, sid: string, key: string) =>
    ['group', cid, sid, key] as const,
  states: (cid: string, sid: string) => ['states', cid, sid] as const,
  stateFindings: (cid: string, sid: string, state: string, f: StateFindingFilters) =>
    ['state-findings', cid, sid, state, f] as const,
  precheck: (cid: string, sid: string) => ['precheck', cid, sid] as const,
  openItems: (cid: string, sid: string) => ['open-items', cid, sid] as const,
};

// After any disposition (single / group / state) the rollups everywhere can move,
// so invalidate the whole customer+session subtree plus the customer switcher
// (which carries per-customer progress).
export function invalidateAfterDisposition(
  client: QueryClient,
  cid: string,
  sid: string,
): void {
  client.invalidateQueries({ queryKey: qk.session(cid) });
  client.invalidateQueries({ queryKey: qk.groups(cid, sid) });
  client.invalidateQueries({ queryKey: ['group', cid, sid] });
  client.invalidateQueries({ queryKey: qk.states(cid, sid) });
  client.invalidateQueries({ queryKey: ['state-findings', cid, sid] });
  client.invalidateQueries({ queryKey: qk.precheck(cid, sid) });
  client.invalidateQueries({ queryKey: qk.myCustomers });
}

export function useMyCustomers(enabled = true) {
  const { identity } = useAuth();
  return useQuery<MyCustomer[]>({
    // Keyed by the signed-in subject and asked only once there is one
    // (BL-596). The customer-entry gate decides which door this person is
    // shown from this list, so it must be THEIR list: one fetched before
    // sign-in (a 401 the gate would then show as a failure) or under whoever
    // signed in last on this browser (a dev sign-out and sign-in never
    // reload the page) is not. The prefix `qk.myCustomers` still invalidates it.
    queryKey: [...qk.myCustomers, identity?.userId ?? null],
    queryFn: () => api.myCustomers(),
    enabled: identity != null && enabled,
  });
}

export function useCostDefaults(cid: string | undefined) {
  return useQuery<CostDefaults>({
    queryKey: ['cost-defaults', cid],
    queryFn: () => api.costDefaults(cid as string),
    enabled: !!cid,
    // Reference data + snapshot-derived prefill: stable for a session, no need to
    // refetch on focus like the review rollups do.
    staleTime: 5 * 60 * 1000,
  });
}

export function useSession(cid: string | undefined, roundId?: string | null) {
  return useQuery<SessionSummary>({
    queryKey: [...qk.session(cid ?? ''), roundId ?? null],
    queryFn: () => api.session(cid as string, roundId),
    refetchInterval: (query) => query.state.data?.signature_pending ? 3000 : false,
    enabled: !!cid,
  });
}

export function useGroups(cid: string | undefined, sid: string | undefined) {
  return useQuery({
    queryKey: qk.groups(cid ?? '', sid ?? ''),
    queryFn: () => api.groups(cid as string, sid as string),
    enabled: !!cid && !!sid,
  });
}

export function useGroup(
  cid: string | undefined,
  sid: string | undefined,
  key: string | null,
) {
  return useQuery({
    queryKey: qk.group(cid ?? '', sid ?? '', key ?? ''),
    queryFn: () => api.group(cid as string, sid as string, key as string),
    enabled: !!cid && !!sid && !!key,
  });
}

export function useStates(cid: string | undefined, sid: string | undefined) {
  return useQuery({
    queryKey: qk.states(cid ?? '', sid ?? ''),
    queryFn: () => api.states(cid as string, sid as string),
    enabled: !!cid && !!sid,
  });
}

export function useStateFindings(
  cid: string | undefined,
  sid: string | undefined,
  state: string | null,
  filters: StateFindingFilters,
) {
  return useQuery({
    queryKey: qk.stateFindings(cid ?? '', sid ?? '', state ?? '', filters),
    queryFn: () =>
      api.stateFindings(cid as string, sid as string, state as string, filters),
    enabled: !!cid && !!sid && !!state,
  });
}

export function useOpenItems(cid: string | undefined, sid: string | undefined) {
  return useQuery({
    queryKey: qk.openItems(cid ?? '', sid ?? ''),
    queryFn: () => api.openItems(cid as string, sid as string),
    enabled: !!cid && !!sid,
  });
}

export function usePrecheck(
  cid: string | undefined,
  sid: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: qk.precheck(cid ?? '', sid ?? ''),
    queryFn: () => api.completePrecheck(cid as string, sid as string),
    enabled: !!cid && !!sid && enabled,
  });
}

// ---- Mutations --------------------------------------------------------------

export function useFindingDisposition(cid: string, sid: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (vars: { findingId: string; body: DispositionInput }) =>
      api.dispositionFinding(cid, sid, vars.findingId, vars.body),
    onSuccess: () => invalidateAfterDisposition(client, cid, sid),
  });
}

export function useGroupDisposition(cid: string, sid: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (vars: { key: string; verdict: import('../api/types').Verdict; body: DispositionInput }) =>
      api.dispositionGroup(cid, sid, vars.key, vars.verdict, vars.body),
    onSuccess: () => invalidateAfterDisposition(client, cid, sid),
  });
}

export function useStateDisposition(cid: string, sid: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (vars: { state: string; body: DispositionInput }) =>
      api.dispositionState(cid, sid, vars.state, vars.body),
    onSuccess: () => invalidateAfterDisposition(client, cid, sid),
  });
}

export function useAddComment(cid: string, sid: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (vars: { findingId: string; body: CommentInput }) =>
      api.addComment(cid, vars.findingId, vars.body),
    onSuccess: () => {
      // Comment count lives on state findings; refresh them.
      client.invalidateQueries({ queryKey: ['state-findings', cid, sid] });
    },
  });
}

export function useComplete(cid: string, sid: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: import('../api/types').CompleteInput) =>
      api.complete(cid, sid, body),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: qk.session(cid) });
      client.invalidateQueries({ queryKey: qk.myCustomers });
    },
  });
}

// ---- Operator console (internal-admin only) --------------------------------
export const opk = {
  workspaces: (archived: boolean, ready = false) => ['op-workspaces', archived, ready] as const,
  workspace: (cid: string) => ['op-workspace', cid] as const,
  coverage: ['op-coverage'] as const,
};

export function useWorkspaces(includeArchived = false, readyToBuild = false) {
  return useQuery({
    queryKey: opk.workspaces(includeArchived, readyToBuild),
    queryFn: () => api.workspaces(includeArchived, readyToBuild),
  });
}

export function useOperatorAttention() {
  return useQuery({
    queryKey: ['operator-attention'],
    queryFn: () => api.operatorAttention(),
  });
}

export function useWorkspace(cid: string | undefined) {
  return useQuery({
    queryKey: opk.workspace(cid ?? ''),
    queryFn: () => api.workspace(cid!),
    enabled: !!cid,
  });
}

export function useCoverage() {
  return useQuery({
    queryKey: opk.coverage,
    queryFn: () => api.coverage(),
    staleTime: 5 * 60_000,
  });
}

/** After any operator change: the customer, the list, and the switcher. */
export function invalidateWorkspace(client: QueryClient, cid?: string): void {
  if (cid) client.invalidateQueries({ queryKey: opk.workspace(cid) });
  client.invalidateQueries({ queryKey: ['op-workspaces'] });
  client.invalidateQueries({ queryKey: qk.myCustomers });
}
